<template>
  <div class="container">
    <BreadcrumbGroup :items="breadcrumbItems" />

    <HeadingOne class="-margin-md">指定口座へのお振込み</HeadingOne>

    <p>
      次回のご返済予定をご確認いただき、当社指定の口座へお振込みをお願いいたします。<br />
      ご利用残高全額のご返済を希望されるお客さまは、ご返済いただける日付によってご返済金額が異なります。<br />
      お手続きの前に必ずコールセンターまでお問い合わせいただくようお願いいたします。<br />
      <small
        >※なお、一度ご連絡をいただいた場合でも当日中にご返済いただけなかった場合はご返済金額が異なります。その場合は、再度ご連絡をいただくようお願いいたします。</small
      >
    </p>

    <ListGroup class="-link">
      <ListItem class="-link" router-name="sv036" target="_blank"
        >お問い合わせはこちら</ListItem
      >
    </ListGroup>

    <section>
      <HeadingTwo>次回のご返済予定</HeadingTwo>

      <section v-if="isOverDue" class="_mb-sm-40 _mb-md-60">
        <notice-box class="-alert">
          <notice-header>
            <h3 class="heading">ご返済予定日を過ぎております。</h3>
          </notice-header>
          <notice-body>
            <p>
              速やかにご返済いただきますようお願いいたします。<br />
              ご返済予定日を過ぎてのお支払いにつきましては、au PAY
              スマートローンの債権管理・回収を委託しているNTS総合弁護士法人までご連絡いただきますようお願いいたします。<br /><br />
              NTS総合弁護士法人<br />
              au PAY スマートローン 債権管理室<br />
              <a href="tel:0570-070-113" class="-underline"
                ><span>0570-070-113</span></a
              >（通話料有料）<br />
              受付時間：9:00～17:30<br />
              （土曜・日曜・祝日、12月30日から1月4日は休み）<br />
              ※番号のおかけ間違いにご注意ください。<br /><br />
              ※一部のIP電話からは、ご利用いただけない場合がございます。繋がらない場合は、以下の番号へお問い合わせください。<br />
              <a href="tel:092-235-5383" class="-underline">092-235-5383</a
              >（通話料有料）
            </p>
          </notice-body>
        </notice-box>
      </section>

      <table-box class="_mb-sm-40 _mb-md-60">
        <table-body>
          <table-row>
            <table-header class="-harfright" colspan="2"
              >次回ご返済金額</table-header
            >
            <table-data class="-pctextright">
              <template v-if="refundFromAccount.repayment_next_refund_amount">
                {{
                  numberToCurrency(
                    refundFromAccount.repayment_next_refund_amount
                  )
                }}
              </template>
              <template v-else> - </template>
            </table-data>
          </table-row>
          <table-row>
            <table-header class="-harfright" colspan="2"
              >次回ご返済日</table-header
            >
            <table-data class="-pctextright">
              <template v-if="refundFromAccount.repayment_next_refund_period">
                {{
                  ymdDateFormat(refundFromAccount.repayment_next_refund_period)
                }}
              </template>
              <template v-else> - </template>
            </table-data>
          </table-row>
        </table-body>
      </table-box>

      <h3 class="heading -black">ご注意事項</h3>
      <ListGroup class="-note">
        <ListItem class="-note"
          >お振込み手数料はお客さまのご負担となります。</ListItem
        >
        <ListItem class="-note"
          >ご返済いただける日付によって、次回のご返済日が異なります。詳しくは下記ページをご確認ください。</ListItem
        >
      </ListGroup>
      <btn-text
        router-name="sv047"
        hash="#section_01"
        class="-next"
        target="_blank"
        >ご返済日について</btn-text
      >

      <HeadingTwo>お客さまのご返済専用口座</HeadingTwo>
      <card-box>
        <card-body>
          <p>
            {{ refundFromAccount.repayment_bank_name }}<br />
            {{ refundFromAccount.repayment_bank_branch_name }}<br />
            （{{ displayrBankDepositType }}）{{
              refundFromAccount.repayment_bank_account_number
            }}<br />
            {{ refundFromAccount.repayment_bank_account_holder }}
          </p>
          <br />
          <p class="-red">
            ※口座番号はお客さまごとに異なります。お間違えのないようご注意ください。<br />
            ※お客さまご本人の名義にてお振込みをお願いいたします。
          </p>
        </card-body>
      </card-box>
    </section>

    <btngroup-single class="-margin-lg">
      <btn-main class="-normal" router-name="mb001">
        会員さま専用サイトトップへ
      </btn-main>
    </btngroup-single>
  </div>
  <!-- container -->
</template>

<script>
import {
  getUserDataLambdaInvoke,
  refundFromAccountLambdaInvoke,
} from "@/utils/LambdaHelper";
import { setMeta } from "@/utils/Meta";
import { numberToCurrency, ymdDateFormat } from "@/utils/Filter";
import { goToErrorPage } from "@/utils/Other";

import HeadingOne from "@/components/heading/HeadingOne";
import ListGroup from "@/components/list/ListGroup";
import ListItem from "@/components/list/ListItem";
import HeadingTwo from "@/components/heading/HeadingTwo";
import CardBox from "@/components/card/CardBox";
import CardBody from "@/components/card/CardBody";
import TableBox from "@/components/tables/TableBox";
import TableBody from "@/components/tables/TableBody";
import TableRow from "@/components/tables/TableRow";
import TableHeader from "@/components/tables/TableHeader";
import TableData from "@/components/tables/TableData";
import BtngroupSingle from "@/components/button/BtngroupSingle";
import BtnMain from "@/components/button/BtnMain";
import BtnText from "@/components/button/BtnText";
import NoticeBox from "@/components/box/NoticeBox";
import NoticeHeader from "@/components/box/NoticeHeader";
import NoticeBody from "@/components/box/NoticeBody";
import BreadcrumbGroup from "@/components/breadcrumb/BreadcrumbGroup";

export default {
  name: "Mb015",
  components: {
    NoticeBody,
    NoticeHeader,
    NoticeBox,
    BtnText,
    BtnMain,
    BtngroupSingle,
    TableData,
    TableHeader,
    TableRow,
    TableBody,
    TableBox,
    CardBody,
    CardBox,
    HeadingTwo,
    ListItem,
    ListGroup,
    HeadingOne,
    BreadcrumbGroup,
  },
  setup() {
    setMeta({
      title: "指定口座へのお振込み｜ご返済",
      description:
        "au PAY スマートローンはau IDをお持ちのお客様のための便利なカードレスローンサービスです。お申込みは、スマートフォン・パソコンでかんたんWEB完結！24時間お申込み可能。最短30分で審査結果をご連絡します。",
      keywords:
        "au PAY スマートローン,au,カードレスローン,スマートフォン,au PAY 残高,セブン銀行スマホATM,借入,返済,手続き,会員,会員サイト,返済,指定口座",
    });
    return {
      ymdDateFormat,
      numberToCurrency,
    };
  },
  data() {
    return {
      breadcrumbItems: [
        {
          text: "トップ",
          routerName: "mb001",
        },
        {
          text: "指定口座へのお振込み",
          active: true,
        },
      ],
      userData: {
        delayed_days: "",
      },
      refundFromAccount: {
        repayment_next_refund_amount: "",
        repayment_next_refund_period: "",
        repayment_bank_name: "",
        repayment_bank_branch_name: "",
        repayment_bank_deposit_type: "",
        repayment_bank_account_number: "",
        repayment_bank_account_holder: "",
      },
    };
  },
  computed: {
    isOverDue() {
      return (
        this.userData.delayed_days !== "" && this.userData.delayed_days !== "0"
      );
    },
    displayrBankDepositType() {
      switch (this.refundFromAccount.repayment_bank_deposit_type) {
        case "1":
          return "普通";
        case "2":
          return "当座";
        case "9":
          return "その他";
        default: // 9
          return "";
      }
    },
  },
  mounted() {
    try {
      try {
        getUserDataLambdaInvoke()
          .then((res) => {
            for (let key in res) {
              if (Object.prototype.hasOwnProperty.call(this.userData, key)) {
                this.userData[key] = res[key];
              }
            }
          })
          .catch((error) => {
            goToErrorPage(error);
          });
      } catch (e) {
        goToErrorPage(e);
      }
      refundFromAccountLambdaInvoke()
        .then((res) => {
          let resBody = JSON.parse(res.body);
          for (let key in resBody) {
            if (
              Object.prototype.hasOwnProperty.call(this.refundFromAccount, key)
            ) {
              this.refundFromAccount[key] = resBody[key];
            }
          }
        })
        .catch((error) => {
          goToErrorPage(error);
        });
    } catch (e) {
      goToErrorPage(e);
    }
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/stylesheets/styles";

.-underline {
  color: $danger;
}

.btn-text {
  &.-next {
    font-size: 14px;
    padding-left: 0;
    @include mq-up(md) {
      font-size: 16px;
    }
    > a:hover {
      color: $orange;
    }
  }
}

.heading.-black {
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 18px;
  @include mq-up(md) {
    font-size: 20px;
    margin-bottom: 25px;
  }
}

.-red {
  color: $danger;
}
</style>
